import { JsPsych, JsPsychPlugin, ParameterType, TrialType } from "jspsych";

import { version } from "../package.json";
import { GroupSessionData, MultiplayerApiLike } from "./multiplayer-api";

const info = <const>{
  name: "multiplayer-sync",
  version: version,
  parameters: {
    /**
     * Predicate evaluated against the full group session on every update. The trial ends as soon
     * as it returns true. Receives the group session data (keyed by participantId). This is the
     * same condition you would pass to `jsPsych.pluginAPI.wait()`.
     */
    wait_for: {
      type: ParameterType.FUNCTION,
      default: undefined,
    },
    /**
     * Data to push into the shared group session when the trial starts, before waiting. Leave
     * null to wait without pushing. As with any jsPsych parameter, you may supply a function that
     * returns the object — useful for reading state set by earlier trials, e.g. `() => ({ offer })`.
     */
    push_data: {
      type: ParameterType.OBJECT,
      default: null,
    },
    /** HTML shown while waiting for the condition to be met. */
    message: {
      type: ParameterType.HTML_STRING,
      default: "<p>Waiting for other players…</p>",
    },
    /**
     * Maximum time to wait, in milliseconds, before giving up. When the timeout elapses the trial
     * ends with `timed_out: true` and `on_timeout` is called. Null (or a non-positive value) waits
     * indefinitely.
     */
    timeout: {
      type: ParameterType.INT,
      default: null,
    },
    /** Called if `timeout` elapses before `wait_for` is satisfied. */
    on_timeout: {
      type: ParameterType.FUNCTION,
      default: null,
    },
    /**
     * Minimum time, in milliseconds, to keep the waiting message on screen. Prevents the screen
     * from flashing by when the condition is already satisfied. Does not extend a wait that takes
     * longer than this on its own.
     */
    minimum_wait: {
      type: ParameterType.INT,
      default: 0,
    },
  },
  data: {
    /** The full group session snapshot at the moment the condition was met (or the timeout fired). */
    group: {
      type: ParameterType.OBJECT,
      default: undefined,
    },
    /** Time spent waiting, in milliseconds, from trial start until the trial ended. */
    wait_time: {
      type: ParameterType.INT,
      default: undefined,
    },
    /** True if the trial ended because `timeout` elapsed rather than because `wait_for` was met. */
    timed_out: {
      type: ParameterType.BOOL,
      default: false,
    },
    /**
     * Message from the `wait()` rejection when the trial ended without `wait_for` being met;
     * null when the condition was satisfied. On a genuine timeout this is the timeout message, so
     * non-timeout rejections (if the API ever produces them) remain diagnosable in the data.
     */
    wait_error: {
      type: ParameterType.STRING,
      default: null,
    },
  },
  // prettier-ignore
  citations: '__CITATIONS__',
};

type Info = typeof info;

/**
 * **multiplayer-sync**
 *
 * A synchronization barrier for multiplayer experiments. Optionally pushes this participant's data
 * into the shared group session, displays a waiting message, and ends the trial once a condition
 * over the group session is met (or an optional timeout elapses). It packages the common
 * push → wait pattern as a single declarative trial so experiments don't have to shoehorn waiting
 * into `call-function` or a `NO_KEYS` keyboard-response trial.
 *
 * Requires a connected multiplayer adapter — call `await jsPsych.pluginAPI.connect(adapter)` before
 * `jsPsych.run()`. The resolved group session is stored in the trial's `group` data so peer reads
 * and role assignment can happen in a normal `on_finish`.
 *
 * @author Hannah Tsukamoto
 * @see {@link https://github.com/jspsych/jspsych-multiplayer/tree/main/packages/plugin-multiplayer-sync multiplayer-sync plugin documentation}
 */
class MultiplayerSyncPlugin implements JsPsychPlugin<Info> {
  static info = info;

  constructor(private jsPsych: JsPsych) {}

  async trial(display_element: HTMLElement, trial: TrialType<Info>, on_load?: () => void) {
    // The multiplayer API is flattened onto pluginAPI by jsPsych core (jsPsych#3694). The published
    // `jspsych` types don't carry it yet, so reach it through the local interface with one cast.
    const api = this.jsPsych.pluginAPI as unknown as MultiplayerApiLike;

    if (typeof trial.wait_for !== "function") {
      throw new Error(
        "multiplayer-sync: the `wait_for` parameter is required and must be a function " +
          "(a predicate over the group session)."
      );
    }

    display_element.innerHTML = `<div class="jspsych-multiplayer-sync">${trial.message}</div>`;
    // jsPsych only auto-fires on_load for trials whose `trial()` returns synchronously; this one
    // returns a Promise, so we must invoke the callback ourselves once the screen is rendered.
    on_load?.();

    const start = performance.now();

    const finish = (group: GroupSessionData, timed_out: boolean, wait_error: string | null) => {
      this.jsPsych.finishTrial({
        group,
        wait_time: Math.round(performance.now() - start),
        timed_out,
        wait_error,
      });
    };

    /** Keep the waiting message on screen until at least `minimum_wait` ms have elapsed. */
    const holdMinimumWait = async () => {
      const elapsed = performance.now() - start;
      if (elapsed < trial.minimum_wait) {
        await new Promise((resolve) => setTimeout(resolve, trial.minimum_wait - elapsed));
      }
    };

    // Push BEFORE the wait try/catch: a push failure is an infrastructure error, not a timeout, and
    // must surface loudly (rejecting the trial) rather than being relabeled as `timed_out: true`.
    if (trial.push_data != null) {
      await api.push(trial.push_data as Record<string, unknown>);
    }

    // Only a positive timeout bounds the wait; null/0/negative means wait indefinitely.
    const timeout =
      typeof trial.timeout === "number" && trial.timeout > 0 ? trial.timeout : undefined;

    try {
      const group = await api.wait(trial.wait_for as (data: GroupSessionData) => boolean, timeout);

      await holdMinimumWait();

      finish(group, false, null);
    } catch (e) {
      // As of jsPsych#3694's current draft, api.wait() rejects only on timeout, so every rejection
      // is labeled `timed_out: true` and routed to on_timeout. The rejection's message is stored in
      // `wait_error` so a non-timeout failure would still be diagnosable from the data.
      // TODO: revisit this seam when #3694 finalizes whether wait() can reject for other reasons.
      if (typeof trial.on_timeout === "function") {
        trial.on_timeout(e);
      }
      // Honour minimum_wait here too, so a short timeout can't flash the waiting message.
      await holdMinimumWait();
      finish(api.getAll(), true, e instanceof Error ? e.message : String(e));
    }
  }
}

export default MultiplayerSyncPlugin;
